import { z } from "zod";
/** Normative primitives shared by the AI EMR foundation v0.1 contracts. */
export const FoundationSemVerSchema = z
    .string()
    .regex(/^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(?:-[0-9A-Za-z.-]+)?(?:\+[0-9A-Za-z.-]+)?$/);
export const FoundationIdentifierSchema = z.string().min(1).max(200);
export const FoundationTimestampSchema = z.string().datetime({ offset: true });
export const FoundationDateSchema = z.string().date();
export const FoundationHashSchema = z
    .object({
    algorithm: z.enum(["sha256", "sha384", "sha512"]),
    value: z.string().regex(/^[A-Fa-f0-9]{64,128}$/),
})
    .strict();
export const CodingSchema = z
    .object({
    system: z.string().url(),
    version: z.string().optional(),
    code: z.string().min(1),
    display: z.string().optional(),
    userSelected: z.boolean().optional(),
})
    .strict();
export const FoundationActorRefSchema = z
    .object({
    actorType: z.enum([
        "practitioner",
        "staff",
        "patient",
        "caregiver",
        "service",
        "agent",
        "organization",
    ]),
    actorId: FoundationIdentifierSchema,
    display: z.string().optional(),
    role: z.string().optional(),
    organizationId: z.string().optional(),
})
    .strict();
export const FoundationPurposeOfUseSchema = z.enum([
    "treatment",
    "payment",
    "operations",
    "patient_access",
    "research",
    "public_health",
    "other",
]);
export const PatientContextRefSchema = z
    .object({
    patientId: FoundationIdentifierSchema,
    tenantId: FoundationIdentifierSchema,
    clinicId: z.string().optional(),
    contextToken: z.string().min(16),
    contextVersion: z.string().min(1),
    purposeOfUse: FoundationPurposeOfUseSchema.optional(),
    expiresAt: FoundationTimestampSchema.optional(),
})
    .strict();
export const FoundationClinicalReferenceSchema = z
    .object({
    resourceType: z.string().min(1),
    resourceId: FoundationIdentifierSchema,
    version: z.string().min(1),
    section: z.string().optional(),
    path: z.string().optional(),
    recordedAt: FoundationTimestampSchema.optional(),
    sourceSystem: z.string().optional(),
    verificationStatus: z
        .enum([
        "clinician_verified",
        "patient_reported",
        "imported_unverified",
        "ai_extracted",
        "conflicting",
        "unknown",
    ])
        .optional(),
    sensitivityLabels: z.array(z.string()).optional(),
})
    .strict();
export const EvidenceReferenceSchema = z
    .object({
    reference: FoundationClinicalReferenceSchema,
    supports: z.string().min(1),
    excerpt: z.string().max(1_000).optional(),
    relevance: z.enum(["direct", "supporting", "contextual", "contradictory"]).optional(),
})
    .strict();
export const CareValidationCheckSchema = z
    .object({
    checkId: z.string(),
    status: z.enum(["pass", "warn", "fail", "not_run", "unknown"]),
    severity: z.enum(["info", "low", "medium", "high", "critical"]),
    message: z.string(),
    code: z.string().optional(),
    validator: z.enum([
        "deterministic_rule",
        "terminology_service",
        "clinical_model",
        "policy_engine",
        "external_service",
        "human",
    ]),
    evidenceRefs: z.array(FoundationClinicalReferenceSchema).optional(),
    blocking: z.boolean().optional(),
    resolvedAt: FoundationTimestampSchema.optional(),
    resolution: z.string().optional(),
})
    .strict();
export const UncertaintySchema = z
    .object({
    uncertaintyId: z.string(),
    statement: z.string(),
    type: z.enum([
        "missing_data",
        "conflicting_data",
        "ambiguous_note",
        "inference",
        "stale_data",
        "unknown_destination_requirement",
        "other",
    ]),
    materiality: z.enum(["non_material", "may_change_action", "blocks_action"]),
    resolutionQuestion: z.string().optional(),
    evidenceRefs: z.array(FoundationClinicalReferenceSchema).optional(),
    resolved: z.boolean().default(false),
})
    .strict();
export const DestinationRefSchema = z
    .object({
    destinationId: z.string(),
    destinationType: z.enum([
        "pharmacy",
        "laboratory",
        "diagnostic_imaging",
        "specialist",
        "hospital",
        "clinic",
        "patient",
        "provider",
        "public_health",
        "payer",
        "other",
    ]),
    display: z.string(),
    organizationId: z.string().optional(),
    endpointId: z.string().optional(),
    channel: z
        .enum([
        "internal",
        "fhir",
        "direct",
        "ereferral",
        "erx",
        "secure_message",
        "fax",
        "print",
        "portal",
        "sms",
        "email",
        "other",
    ])
        .optional(),
    directoryVersion: z.string().optional(),
    verifiedAt: FoundationTimestampSchema.optional(),
})
    .strict();
export const AttachmentRefSchema = z
    .object({
    documentId: z.string(),
    version: z.string(),
    title: z.string(),
    contentType: z.string().optional(),
    contentHash: FoundationHashSchema,
    selectionReason: z.string(),
    disclosureLabels: z.array(z.string()).optional(),
    pageSelection: z.array(z.number().int().min(1)).optional(),
})
    .strict();
export const ApprovalRequirementSchema = z
    .object({
    policyId: z.string(),
    policyVersion: z.string().optional(),
    level: z.enum(["none", "review", "attest", "sign", "dual_sign", "restricted"]),
    requiredRoles: z.array(z.string()).min(1),
    allowBulkApproval: z.boolean().optional(),
    requiresWarningAcknowledgement: z.boolean().optional(),
    approvalTtlSeconds: z.number().int().min(1).optional(),
    reason: z.string().optional(),
})
    .strict();
export const ExecutionPolicySchema = z
    .object({
    mode: z.enum([
        "draft_only",
        "prepare_then_commit",
        "commit_after_approval",
        "background_after_approval",
        "manual_only",
    ]),
    idempotencyScope: z.enum(["action", "bundle", "patient_day", "external_transaction"]),
    revalidateBeforeCommit: z.boolean(),
    requiresExactPayloadHash: z.boolean().optional(),
    requiresDestinationPinning: z.boolean().optional(),
    requiresAttachmentPinning: z.boolean().optional(),
    transactionTimeoutSeconds: z.number().int().min(1).optional(),
    retryPolicyId: z.string().optional(),
})
    .strict();
export const OwnerAssignmentSchema = z
    .object({
    primaryOwner: FoundationActorRefSchema,
    backupOwner: FoundationActorRefSchema.optional(),
    poolId: z.string().optional(),
    coveragePolicyId: z.string(),
    assignedAt: FoundationTimestampSchema.optional(),
})
    .strict();
export const FollowUpTemplateSchema = z
    .object({
    required: z.boolean(),
    expectedEventType: z.enum([
        "none",
        "lab_result",
        "imaging_report",
        "referral_response",
        "consult_report",
        "medication_monitoring",
        "appointment_completion",
        "patient_acknowledgement",
        "delivery_receipt",
        "form_response",
        "other",
    ]),
    targetDuration: z.string().regex(/^P/).optional(),
    ownerAssignment: OwnerAssignmentSchema.optional(),
    escalationPolicyId: z.string().optional(),
    closurePolicyId: z.string().optional(),
})
    .strict();
export const ArtifactSignatureSchema = z
    .object({
    algorithm: z.enum(["EdDSA", "ES256", "ES384", "RS256", "PS256"]),
    keyId: z.string(),
    value: z.string().min(16),
    signedAt: FoundationTimestampSchema,
    certificateThumbprint: z.string().optional(),
})
    .strict();
export const FoundationAuditContextSchema = z
    .object({
    requestId: z.string(),
    traceId: z.string(),
    spanId: z.string().optional(),
    actor: FoundationActorRefSchema,
    purposeOfUse: z.string(),
    breakGlass: z.boolean().optional(),
    breakGlassReason: z.string().optional(),
    clientIpHash: z.string().optional(),
    sessionId: z.string().optional(),
})
    .strict();
//# sourceMappingURL=foundation.js.map